import React, { createContext, useState, useContext, useEffect } from 'react';
import { programsAPI } from '../services/api';

const ProgramContext = createContext(null);

const DEFAULT_SLUG = 'freedom';
const STORAGE_KEY = 'active_program';

export const useProgram = () => {
  const ctx = useContext(ProgramContext);
  if (!ctx) {
    throw new Error('useProgram must be used within ProgramProvider');
  }
  return ctx;
};

// Mirrors backend logic: ?program → localStorage → hostname → default.
const resolveSlug = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get('program');
    if (fromQuery) return fromQuery.toLowerCase();

    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return stored.toLowerCase();

    const host = window.location.hostname;
    if (host) {
      const parts = host.split('.');
      // Only treat first segment as program slug when it's a real subdomain
      // (e.g. freedom.lifyup.com.br → 4 parts). Apex (lifyup.com.br → 3) uses default.
      if (parts.length >= 4) {
        const sub = parts[0];
        if (sub && !['www', 'api', 'admin', 'localhost', '127'].includes(sub)) {
          return sub.toLowerCase();
        }
      }
    }
  } catch (_) {
    // fall through
  }
  return DEFAULT_SLUG;
};

// Maps program colors → CSS custom properties consumed by global.css / components.
const applyBranding = (program) => {
  if (!program) return;
  const root = document.documentElement;
  const set = (key, value) => {
    if (value) root.style.setProperty(key, value);
  };

  set('--primary-500', program.primary_color);
  set('--secondary-500', program.secondary_color);
  set('--success-500', program.success_color);
  set('--danger-500', program.danger_color);
  set('--text-primary', program.text_primary);
  set('--text-secondary', program.text_secondary);

  if (program.label) {
    document.title = program.label;
  }
  if (program.favicon_url) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = program.favicon_url;
  }
};

export const ProgramProvider = ({ children }) => {
  const [slug, setSlug] = useState(resolveSlug);
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    programsAPI
      .getBySlug(slug)
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data;
        setProgram(data);
        applyBranding(data);
        try {
          localStorage.setItem(STORAGE_KEY, slug);
        } catch (_) {}
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Falha ao carregar programa:', err);
        setError(err);
        // Fallback to default if a non-default slug fails.
        if (slug !== DEFAULT_SLUG) {
          setSlug(DEFAULT_SLUG);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const switchProgram = (nextSlug) => {
    if (!nextSlug) return;
    const normalized = String(nextSlug).toLowerCase();
    try {
      localStorage.setItem(STORAGE_KEY, normalized);
    } catch (_) {}
    setSlug(normalized);
  };

  const value = {
    slug,
    program,
    loading,
    error,
    switchProgram,
  };

  return <ProgramContext.Provider value={value}>{children}</ProgramContext.Provider>;
};

export default ProgramContext;
